/**
 * Web Component: <exp-share-story>
 * Handles interactive sharing actions:
 * - Email: mailto link with article title and URL
 * - Facebook: centered popup dialog
 * - Copy Link: clipboard API with visual tooltip feedback
 */
class ExpShareStory extends HTMLElement {
  constructor() {
    super();
    this.copyBtn = null;
    this.fbBtn = null;
    this.emailBtn = null;
    this.copyTimeout = null;
  }

  connectedCallback() {
    this.copyBtn = this.querySelector('[data-exp-copy-link]');
    this.fbBtn = this.querySelector('[data-exp-share-facebook]');
    this.emailBtn = this.querySelector('[data-exp-share-email]');

    this.bindEvents();
    this.syncCurrentUrl();
  }

  disconnectedCallback() {
    if (this.copyTimeout) {
      clearTimeout(this.copyTimeout);
    }
  }

  syncCurrentUrl() {
    const currentUrl = window.location.href;
    const currentTitle = document.title || '';

    // Update Facebook share link if URL is dynamic
    if (this.fbBtn && (!this.fbBtn.href || this.fbBtn.href === '#' || this.fbBtn.href.endsWith('u='))) {
      this.fbBtn.href = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(currentUrl)}`;
    }

    // Update Email share link if URL is dynamic
    if (this.emailBtn && (!this.emailBtn.href || this.emailBtn.href === '#' || this.emailBtn.href.startsWith('mailto:?'))) {
      const subject = encodeURIComponent(currentTitle);
      const body = encodeURIComponent(`${currentTitle}\n\n${currentUrl}`);
      this.emailBtn.href = `mailto:?subject=${subject}&body=${body}`;
    }
  }

  bindEvents() {
    // Copy Link Handler
    if (this.copyBtn) {
      this.copyBtn.addEventListener('click', (e) => {
        e.preventDefault();
        this.handleCopyLink();
      });
    }

    // Facebook Popup Handler
    if (this.fbBtn) {
      this.fbBtn.addEventListener('click', (e) => {
        const href = this.fbBtn.href;
        if (href && href.startsWith('http')) {
          e.preventDefault();
          const width = 600;
          const height = 500;
          const left = Math.max(0, (window.innerWidth - width) / 2 + window.screenX);
          const top = Math.max(0, (window.innerHeight - height) / 2 + window.screenY);
          window.open(
            href,
            'fb-share',
            `width=${width},height=${height},left=${left},top=${top},toolbar=no,menubar=no,scrollbars=yes,resizable=yes`
          );
        }
      });
    }
  }

  async handleCopyLink() {
    const itemWrapper = this.copyBtn.closest('.exp-share-story__item') || this.copyBtn.parentElement;
    const urlToCopy = this.copyBtn.getAttribute('data-url') || window.location.href;

    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(urlToCopy);
      } else {
        // Fallback for older browsers / webviews
        const textarea = document.createElement('textarea');
        textarea.value = urlToCopy;
        textarea.style.position = 'fixed';
        textarea.style.opacity = '0';
        document.body.appendChild(textarea);
        textarea.focus();
        textarea.select();
        document.execCommand('copy');
        document.body.removeChild(textarea);
      }

      this.showCopiedState(itemWrapper);
    } catch (err) {
      console.warn('Failed to copy link automatically:', err);
    }
  }

  showCopiedState(wrapper) {
    if (!wrapper) return;

    if (this.copyTimeout) {
      clearTimeout(this.copyTimeout);
    }

    wrapper.classList.add('is-copied');

    this.copyTimeout = setTimeout(() => {
      wrapper.classList.remove('is-copied');
    }, 2000);
  }
}

if (!customElements.get('exp-share-story')) {
  customElements.define('exp-share-story', ExpShareStory);
}
